import React from "react";
import { Link } from "react-router-dom";

const VARIANTS = {
  filled:
    "text-white border border-transparent rounded-lg linear bg-[linear-gradient(90deg,_#B53087_0%,_#F96011_50%,_#B53087_100%)] bg-[length:200%_100%] bg-left hover:bg-right",
  outlined:
    "relative overflow-hidden bg-transparent text-white ring-1 rounded-lg linear ring-white hover:text-white hover:ring-transparent active:ring-white before:content-[''] before:absolute before:inset-0 before:rounded-sm before:pointer-events-none before:bg-[linear-gradient(90deg,_#B53087_0%,_#F96011_50%,_#B53087_100%)] before:bg-[length:200%_100%] before:bg-left before:opacity-0 before:transition-all before:duration-500 before:ease-in-out before:z-[-1] hover:before:opacity-100 hover:before:bg-right",
  link: "bg-transparent text-white p-0 h-auto align-baseline  ring-0 border-0 ",
};

const SIZES = {
  sm: "h-8 px-4 py-2",
  md: "h-9 px-6 py-3",
  lg: "h-12 px-8 py-4 text-lg",
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function CustomButton({
  children,
  variant = "filled",
  size = "md",
  className = "",
  disableHover = false,
  fullWidth = false,
  disabled = false,
  active = false,
  to,
  href,
  target,
  rel,
  type = "button",
  onClick,
  ...props
}) {
  const removeHoverClasses = (classes) => {
    if (!classes) return classes;
    return classes
      .split(" ")
      .filter((token) => token && !token.includes("hover:"))
      .join(" ");
  };

  const base =
    "inline-flex cursor-pointer transition-all duration-500 items-center justify-center align-middle text-base leading-5 whitespace-nowrap";

  const disabledStyles =
    "opacity-50 cursor-not-allowed pointer-events-none hover:none active:none";

  const activeStyles = active
    ? variant === "filled"
      ? "bg-neutral-800"
      : "border-neutral-300 text-neutral-200"
    : "";

  const isRouterLink = Boolean(to);
  const isExternalLink = Boolean(href && !to);
  const Component = isRouterLink ? Link : isExternalLink ? "a" : "button";

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) onClick(e);
  };

  const variantClasses = VARIANTS[variant] || VARIANTS.filled;
  const sanitizedVariantClasses = disableHover
    ? removeHoverClasses(variantClasses)
    : variantClasses;
  const sanitizedPassedClassName = disableHover
    ? removeHoverClasses(className)
    : className;

  const computedClassName = cx(
    base,
    variant === "link" ? "" : SIZES[size] || SIZES.md,
    sanitizedVariantClasses,
    activeStyles,
    disabled && disabledStyles,
    fullWidth && "w-full",
    sanitizedPassedClassName,
  );

  const commonProps = {
    className: computedClassName,
    onClick: handleClick,
    ...(disabled ? { "aria-disabled": true, tabIndex: -1 } : {}),
    ...props,
  };

  if (Component === Link) {
    return (
      <Link to={to} target={target} rel={rel} {...commonProps}>
        {children}
      </Link>
    );
  }

  if (Component === "a") {
    const externalRel =
      target === "_blank" && !rel ? "noopener noreferrer" : rel;
    return (
      <a href={href} target={target} rel={externalRel} {...commonProps}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} disabled={disabled} {...commonProps}>
      {children}
    </button>
  );
}
